import { apiClient } from './client';

// ============================================================================
// Types
// ============================================================================

export interface SweepstakesContest {
  id: string;
  collection_id: string | null;
  contest_type: 'sweepstakes' | 'mint_rewards';
  launch_id: string | null;
  name: string;
  description: string | null;
  status: 'draft' | 'scheduled' | 'snapshot_pending' | 'active' | 'ended' | 'resolved';
  snapshot_at: string | null;
  starts_at: string;
  ends_at: string;
  min_price_drops: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  proof_links: string[];
  drawn_by: 'auto' | 'manual' | null;
}

export interface PrizeTier {
  id: string;
  sweepstakes_id: string;
  rank: number;
  name: string;
  description: string;
  type: 'leaderboard' | 'raffle';
  winner_count: number;
}

export interface LeaderboardRow {
  wallet_address: string;
  entry_count: number;
  latest_entry_at: string;
  rank: number;
}

export interface SweepstakesWinner {
  id: string;
  sweepstakes_id: string;
  prize_tier_id: string;
  wallet_address: string;
  winning_entry_id: string | null;
  rank: number | null;
  drawn_at: string;
  tier_name: string;
  tier_type: 'leaderboard' | 'raffle';
  tier_rank: number;
}

export interface ContestView {
  contest: SweepstakesContest;
  prizeTiers: PrizeTier[];
  stats: {
    participants: number;
    entries: number;
    eligibleOffers: number;
  };
  winners?: SweepstakesWinner[];
}

export interface LeaderboardView {
  leaderboard: LeaderboardRow[];
  myRank?: { rank: number; entry_count: number } | null;
  totalParticipants: number;
  totalEntries: number;
}

export interface CreateSweepstakesInput {
  collectionId: string;
  name: string;
  description?: string;
  snapshotAt: string;
  startsAt: string;
  endsAt: string;
  minPriceXrp?: number;
  prizeTiers: Array<{
    name: string;
    description: string;
    type: 'leaderboard' | 'raffle';
    winnerCount: number;
  }>;
}

// ============================================================================
// API Functions
// ============================================================================

export const sweepstakesApi = {
  // Public endpoints
  getForCollection: (collectionId: string) =>
    apiClient.get<ContestView | { contest: null }>(`/sweepstakes/collection/${collectionId}`),

  getBySlug: (slug: string) =>
    apiClient.get<ContestView | { contest: null }>(`/sweepstakes/by-slug/${slug}`),

  getById: (id: string) =>
    apiClient.get<ContestView>(`/sweepstakes/${id}`),

  getLeaderboard: (id: string, wallet?: string) =>
    apiClient.get<LeaderboardView>(`/sweepstakes/${id}/leaderboard${wallet ? `?wallet=${wallet}` : ''}`),

  getEligibleOffers: (id: string) =>
    apiClient.get<{ offerIds: string[] }>(`/sweepstakes/${id}/eligible-offers`),

  getMyEntries: (id: string) =>
    apiClient.get<{ entryCount: number; rank: number | null }>(`/sweepstakes/${id}/my-entries`),

  // Management endpoints
  getForCollectionManage: (collectionId: string) =>
    apiClient.get<(ContestView | { contest: null }) & { pastContests?: ContestView[] }>(`/manage/sweepstakes/collection/${collectionId}`),

  create: (data: CreateSweepstakesInput) =>
    apiClient.post<{ contest: SweepstakesContest; prizeTiers: PrizeTier[] }>('/manage/sweepstakes', data),

  update: (id: string, data: Partial<CreateSweepstakesInput>) =>
    apiClient.put<{ contest: SweepstakesContest; prizeTiers: PrizeTier[] }>(`/manage/sweepstakes/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/manage/sweepstakes/${id}`),

  publish: (id: string) =>
    apiClient.post<{ contest: SweepstakesContest }>(`/manage/sweepstakes/${id}/publish`),

  drawWinners: (id: string) =>
    apiClient.post<{ winners: SweepstakesWinner[] }>(`/manage/sweepstakes/${id}/draw`),

  resolve: (id: string, proofLinks?: string[]) =>
    apiClient.post<{ contest: SweepstakesContest }>(`/manage/sweepstakes/${id}/resolve`, { proofLinks }),

  getStats: (id: string) =>
    apiClient.get<{ participants: number; entries: number; eligibleOffers: number }>(`/manage/sweepstakes/${id}/stats`),
};
